import { Flex, Text } from '@chakra-ui/react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { MangoModal } from '~/components/shared/MangoModal'
import { Size } from '~/types/ThemeTypes'

interface RestartConfirmationModalProps {
  isOpen: boolean
  onConfirm: () => void
  onCancel: () => void
  channelName: string
  isLoading?: boolean
  downloadStage?: 'checking' | 'downloading' | 'activating' | null
  downloadProgress?: number
}

export const RestartConfirmationModal: React.FC<
  RestartConfirmationModalProps
> = ({
  isOpen,
  onConfirm,
  onCancel,
  channelName,
  isLoading = false,
  downloadStage = null,
  downloadProgress = 0,
}) => {
  const { t } = useTranslation()

  return (
    <MangoModal
      isOpen={isOpen}
      onClose={onCancel}
      closeButton={!isLoading}
      closeOnInteractOutside={!isLoading}
      closeOnEscape={!isLoading}
      header={t('updateSettings.channelSwitcher.restartModal.title')}
      body={
        isLoading ? (
          <>
            <Text>
              {t(
                'updateSettings.channelSwitcher.restartModal.switchingChannel',
                {
                  channelName,
                  defaultValue: `Switching to ${channelName}...`,
                },
              )}
            </Text>
            {downloadStage === 'checking' && (
              <Text marginTop={2} fontSize="sm" color="fg.muted">
                {t(
                  'updateSettings.channelSwitcher.restartModal.checkingUpdates',
                  {
                    defaultValue: 'Checking for updates...',
                  },
                )}
              </Text>
            )}
            {downloadStage === 'downloading' && (
              <Text marginTop={2} fontSize="sm" color="fg.muted">
                {t(
                  'updateSettings.channelSwitcher.restartModal.downloadingUpdate',
                  {
                    progress: Math.round(downloadProgress),
                    defaultValue: `Downloading update... ${Math.round(downloadProgress)}%`,
                  },
                )}
              </Text>
            )}
            {downloadStage === 'activating' && (
              <Text marginTop={2} fontSize="sm" color="fg.muted">
                {t(
                  'updateSettings.channelSwitcher.restartModal.activatingChannel',
                  {
                    defaultValue: 'Activating channel...',
                  },
                )}
              </Text>
            )}
          </>
        ) : (
          <Text marginTop={2}>
            {t('updateSettings.channelSwitcher.restartModal.warningMessage')}
          </Text>
        )
      }
      footer={
        <Flex gap={2}>
          <MangoButton
            size={Size.sm}
            variant={MangoButtonVariant.Default_200}
            onClick={onCancel}
            disabled={isLoading}
          >
            {t('updateSettings.channelSwitcher.restartModal.cancel')}
          </MangoButton>
          <MangoButton
            size={Size.sm}
            variant={MangoButtonVariant.Destructive_300}
            onClick={onConfirm}
            isLoading={isLoading}
            disabled={isLoading}
          >
            {isLoading
              ? t('updateSettings.channelSwitcher.restartModal.switching', {
                  defaultValue: 'Switching...',
                })
              : t('updateSettings.channelSwitcher.restartModal.restartNow')}
          </MangoButton>
        </Flex>
      }
    />
  )
}
